import { Cart, ProductPrice, SalesOrder } from "@msdyn365-commerce/retail-proxy";
import { ProductSearchResultExt, SimpleProductExt } from "../types";
import { ProductListProduct } from "../../components/product-list-component/product-list-component";
import { getDiscountLines, getOriginalPrice } from "../../themes/dlvry-adventureworks/views/components/price.component";

function isDataLayerAvailable() {
    return typeof window !== 'undefined' && (window as any).dataLayer;
}

type BaseEvent = {
    event: 'add_to_cart'
    | 'remove_from_cart'
    | 'purchase'
    | 'view_cart'
    | 'begin_checkout'
    | 'search'
    | 'begin_sign_up'
    | 'sign_up'
    | 'view_item'
    | 'begin_alt_product_replaced'
    | 'alt_product_replaced';
    eventCallback?: () => void;
};

type CartEvent = BaseEvent & {
    event: 'add_to_cart' | 'remove_from_cart' | 'view_cart' | 'view_item';
    ecommerce: {
        currency: string;
        value: number;
        items: IECommerceItem[];
    };
};

type BeginCheckoutEvent = BaseEvent & {
    event: 'begin_checkout';
    ecommerce: {
        currency: string;
        value: number;
        coupon?: string;
        items: IECommerceItem[];
    };
};

type PurchaseEvent = BaseEvent & {
    event: 'purchase';
    ecommerce: {
        transaction_id: string;
        value: number;
        tax?: number;
        shipping?: number;
        currency: string;
        coupon?: string;
        items: IECommerceItem[];
    };
};

type SearchEvent = BaseEvent & {
    event: 'search';
    search_term: string;
};

type SignUpEvent = BaseEvent & {
    event: 'sign_up';
    method: string; // e.g., 'Direct', 'Google', 'Facebook'
};

type BeginSignUpEvent = BaseEvent & {
    event: 'begin_sign_up';
};

type BeginAltProductReplacedEvent = BaseEvent & {
    event: 'begin_alt_product_replaced';
    original_item_id: string;
};

type AltProductReplacedEvent = BaseEvent & {
    event: 'alt_product_replaced';
    original_item_id: string;
    original_item_classification: string;
    alt_item_id: string;
    alt_item_classification: string;
    recommendation_type: string;
    position: number;
};

type GTMEvent = BaseEvent | CartEvent | PurchaseEvent | BeginCheckoutEvent | SearchEvent | SignUpEvent | BeginSignUpEvent | BeginAltProductReplacedEvent | AltProductReplacedEvent;

interface IECommerceItem {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}

async function sendToDataLayerAsync(event: GTMEvent): Promise<void> {
    return new Promise((resolve) => {
        try {
            const dataLayer = (window as any).dataLayer;

            event.eventCallback = () => {
                resolve();
            };

            const debugMode = typeof window !== 'undefined' && window.location.search && new URLSearchParams(window.location.search).has('debugGTM');

            if (isDataLayerAvailable()) {
                dataLayer.push({ ecommerce: undefined }); // Clear previous ecommerce data, this is required to avoid duplicate data
                dataLayer.push(event);

                if (debugMode) {
                    console.log('GTM Event:', event);
                }

                // Call resolve after 2 seconds to prevent blocking the page load
                setTimeout(() => resolve(), 2000);
            } else {
                console.log('GTM Event:', event);
                resolve();
            }
        } catch (error) {
            console.error('Error pushing GTM event to dataLayer:', error);
            resolve();
        }
    });
}

type ConsentValue = 'granted' | 'denied';

interface IConsentMode {
    ad_storage: ConsentValue;
    analytics_storage: ConsentValue;
    ad_user_data: ConsentValue;
    ad_personalization: ConsentValue;
    personalization_storage: ConsentValue;
    functionality_storage: ConsentValue;
    security_storage: ConsentValue;
}

function saveConsentToCookies(consentMode: IConsentMode) {
    document.cookie = `ga_consentMode=${encodeURIComponent(JSON.stringify(consentMode))}; SameSite=None; Secure; path=/; max-age=${60 * 60 * 24 * 4000}`;
}

export function consent(consentMode: IConsentMode) {
    localStorage.setItem('consentMode', JSON.stringify(consentMode));
    saveConsentToCookies(consentMode);

    console.log('Updating consent mode in gtag:', consentMode);
    if ((window as any).gtag) {
        (window as any).gtag('consent', 'update', consentMode);
    }
}

export function consentAll() {
    consent({
        ad_storage: 'granted',
        analytics_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        personalization_storage: 'granted',
        functionality_storage: 'granted',
        security_storage: 'granted'
    });
}

export function consentNone() {
    consent({
        ad_storage: 'denied',
        analytics_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        personalization_storage: 'denied',
        functionality_storage: 'denied',
        security_storage: 'granted' // security_storage is set to granted to ensure GTM can still function to respect the user's consent choice and not block the loading of GTM scripts
    });
}

export function getConsentMode(): IConsentMode | undefined {
    const consentMode = localStorage.getItem('consentMode');
    if (consentMode) {
        try {
            saveConsentToCookies(JSON.parse(consentMode) as IConsentMode);
            return JSON.parse(consentMode) as IConsentMode;
        } catch (error) {
            return undefined;
        }
    }
    return undefined;
}

export async function updateCartAsync(cart: Cart, cartLinesToUpdate: Record<string, number>, products: SimpleProductExt[], prices: ProductPrice[]) {
    try {
        let addIndex = 0;
        let removeIndex = 0;

        const adds: IECommerceItem[] = [];
        const removes: IECommerceItem[] = [];

        for (const cartLine of Object.entries(cartLinesToUpdate)) {
            const [lineId, quantity] = cartLine;
            const cartLineItem = cart.CartLines?.find(line => line.LineId === lineId);
            const product = products.find(p => p.ItemId === cartLineItem?.ItemId);
            const price = prices.find(p => p.ProductId === product?.RecordId);

            if (!cartLineItem || !product || !price) {
                console.warn(`Cart line or product not found for LineId: ${lineId}`);
                continue;
            }

            const higher = quantity && cartLineItem.Quantity && quantity > cartLineItem.Quantity;
            const difference = quantity && cartLineItem.Quantity ? Math.abs(quantity - cartLineItem.Quantity) : 0;
            const newItem: IECommerceItem = {
                item_id: cartLineItem.ItemId || 'Unknown',
                item_name: product.Name || 'Unknown',
                discount: getOriginalPrice(price) - (price.CustomerContextualPrice || 0),
                item_brand: product.Brand,
                item_category: product.Category,
                price: price.CustomerContextualPrice || 0,
                quantity: difference
            };

            const categorySegments = product.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            if (higher) {
                newItem.index = addIndex++;
                adds.push(newItem);
            } else {
                newItem.index = removeIndex++;
                removes.push(newItem);
            }
        }

        if (adds.length > 0) {
            await sendToDataLayerAsync({
                event: 'add_to_cart',
                ecommerce: {
                    currency: 'NOK',
                    value: adds.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                    items: adds
                }
            });
        }

        if (removes.length > 0) {
            await sendToDataLayerAsync({
                event: 'remove_from_cart',
                ecommerce: {
                    currency: 'NOK',
                    value: removes.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                    items: removes
                }
            });
        }
    } catch (error) {
        console.error('Error updating cart:', error);
    }
}

export async function addToCartAsync(product: SimpleProductExt | ProductSearchResultExt | ProductListProduct, quantity: number) {
    try {
        const items: IECommerceItem[] = [];

        for (const p of [product]) {
            const newItem: IECommerceItem = {
                item_id: p.ItemId || 'Unknown',
                item_name: p.Name || 'Unknown',
                item_brand: p.Brand,
                item_category: p.Category,
                price: p.Price || 0,
                quantity
            };

            const categorySegments = p.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'add_to_cart',
            ecommerce: {
                currency: 'NOK',
                value: items.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                items
            }
        });
    } catch (error) {
        console.error('Error pushing add_to_cart event to dataLayer:', error);
    }
}

export async function removeFromCartAsync(product: SimpleProductExt | ProductSearchResultExt | ProductListProduct, quantity: number) {
    try {
        const items: IECommerceItem[] = [];

        for (const p of [product]) {
            const newItem: IECommerceItem = {
                item_id: p.ItemId || 'Unknown',
                item_name: p.Name || 'Unknown',
                item_brand: p.Brand,
                item_category: p.Category,
                price: p.Price || 0,
                quantity
            };

            const categorySegments = p.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'remove_from_cart',
            ecommerce: {
                currency: 'NOK',
                value: items.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                items
            }
        });
    } catch (error) {
        console.error('Error pushing remove_from_cart event to dataLayer:', error);
    }
}

export async function removeProductsFromCartAsync(products: (SimpleProductExt | ProductSearchResultExt | ProductListProduct)[], quantities: { [recordId: number]: number }) {
    try {
        const items: IECommerceItem[] = [];

        for (const product of products) {
            const quantity = quantities[product.RecordId] || 1; // Default to 1 if not specified
            const newItem: IECommerceItem = {
                item_id: product.ItemId || 'Unknown',
                item_name: product.Name || 'Unknown',
                item_brand: product.Brand,
                item_category: product.Category,
                price: product.Price || 0,
                quantity
            };

            const categorySegments = product.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'remove_from_cart',
            ecommerce: {
                currency: 'NOK',
                value: items.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                items
            }
        });
    } catch (error) {
        console.error('Error removing products from cart:', error);
    }
}

export async function purchaseEventAsync(salesOrder: SalesOrder, products: SimpleProductExt[]) {
    try {
        const items: IECommerceItem[] = [];
        let i = 0;

        for (const line of salesOrder.SalesLines || []) {
            const product = products.find(p => p.ItemId === line.ItemId);
            const newItem: IECommerceItem = {
                item_id: line.ItemId || 'Unknown',
                item_name: product?.Name || 'Unknown',
                discount: (line.DiscountAmountWithoutTax || 0) / (line.Quantity || 1),
                index: i++,
                item_brand: product?.Brand,
                item_category: product?.Category,
                price: (line.NetAmount || 0) / (line.Quantity || 1),
                quantity: line.Quantity
            };

            const categorySegments = product?.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'purchase',
            ecommerce: {
                transaction_id: salesOrder.Id,
                value: salesOrder.NetAmountWithoutTax || 0,
                currency: 'NOK',
                tax: salesOrder.TaxAmount || 0,
                shipping: salesOrder.ShippingChargeAmount || 0,
                items
            }
        });
    } catch (error) {
        console.error('Error pushing purchase event to dataLayer:', error);
    }
}

export async function viewCartEventAsync(cart: Cart, products: SimpleProductExt[], prices: ProductPrice[]) {
    try {
        const items: IECommerceItem[] = [];
        let i = 0;

        for (const line of cart.CartLines || []) {
            const product = products.find(p => p.ItemId === line.ItemId);
            const price = prices.find(p => p.ProductId === product?.RecordId);

            if (!product || !price) {
                console.warn(`Product or price not found for ItemId: ${line.ItemId}`);
                continue;
            }

            const newItem: IECommerceItem = {
                item_id: line.ItemId || 'Unknown',
                item_name: product?.Name || 'Unknown',
                discount: getOriginalPrice(price) - (price.CustomerContextualPrice || 0),
                index: i++,
                item_brand: product?.Brand,
                item_category: product?.Category,
                price: price.CustomerContextualPrice || 0,
                quantity: line.Quantity
            };

            const categorySegments = product?.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'view_cart',
            ecommerce: {
                currency: 'NOK',
                value: items.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                items
            }
        });
    } catch (error) {
        console.error('Error pushing view_cart event to dataLayer:', error);
    }
}

export async function beginCheckoutEventAsync(cart: Cart, products: SimpleProductExt[]) {
    try {
        const items: IECommerceItem[] = [];
        let i = 0;

        for (const line of cart.CartLines || []) {
            const product = products.find(p => p.ItemId === line.ItemId);

            if (!product) {
                console.warn(`Product or price not found for ItemId: ${line.ItemId}`);
                continue;
            }

            const newItem: IECommerceItem = {
                item_id: line.ItemId || 'Unknown',
                item_name: product?.Name || 'Unknown',
                discount: getDiscountLines(line.DiscountLines).reduce((sum, discount) => sum + (discount.EffectiveAmount || 0), 0) / (line.Quantity || 1),
                index: i++,
                item_brand: product?.Brand,
                item_category: product?.Category,
                price: (line.NetAmountWithoutTax || 0) / (line.Quantity || 1),
                quantity: line.Quantity
            };

            const categorySegments = product?.CategoryPaths?.[0].Segments;

            if (categorySegments) {
                while (categorySegments.length > 5) {
                    // Remove second to last category until we have 5 or less
                    categorySegments.splice(categorySegments.length - 2, 1);
                }

                for (let j = 0; j < categorySegments.length; j++) {
                    const suffix = j === 0 ? '' : j + 1;
                    const categoryKey = `item_category${suffix}`;
                    newItem[categoryKey] = categorySegments[j].CategoryName;
                }
            }

            items.push(newItem);
        }

        await sendToDataLayerAsync({
            event: 'begin_checkout',
            ecommerce: {
                currency: 'NOK',
                value: items.reduce((sum, p) => sum + ((p.price || 0) * (p.quantity || 1)), 0),
                items
            }
        });
    } catch (error) {
        console.error('Error pushing begin_checkout event to dataLayer:', error);
    }
}

export async function searchEventAsync(searchTerm: string) {
    try {
        await sendToDataLayerAsync({
            event: 'search',
            search_term: searchTerm
        });
    } catch (error) {
        console.error('Error pushing search event to dataLayer:', error);
    }
}

export async function signUpEventAsync() {
    try {
        await sendToDataLayerAsync({
            event: 'sign_up',
            method: 'Direct'
        });
    } catch (error) {
        console.error('Error pushing sign_up event to dataLayer:', error);
    }
}

export async function beginSignUpEventAsync() {
    try {
        await sendToDataLayerAsync({
            event: 'begin_sign_up'
        });
    } catch (error) {
        console.error('Error pushing begin_sign_up event to dataLayer:', error);
    }
}

export async function viewItemEventAsync(product: SimpleProductExt | ProductSearchResultExt | ProductListProduct, price: ProductPrice) {
    try {
        const newItem: IECommerceItem = {
            item_id: product.ItemId || 'Unknown',
            item_name: product.Name || 'Unknown',
            item_brand: product.Brand,
            item_category: product.Category,
            price: price.CustomerContextualPrice || 0,
            quantity: 1,
            discount: getOriginalPrice(price) - (price.CustomerContextualPrice || 0)
        };

        const categorySegments = product?.CategoryPaths?.[0].Segments;

        if (categorySegments) {
            while (categorySegments.length > 5) {
                // Remove second to last category until we have 5 or less
                categorySegments.splice(categorySegments.length - 2, 1);
            }

            for (let j = 0; j < categorySegments.length; j++) {
                const suffix = j === 0 ? '' : j + 1;
                const categoryKey = `item_category${suffix}`;
                newItem[categoryKey] = categorySegments[j].CategoryName;
            }
        }

        await sendToDataLayerAsync({
            event: 'view_item',
            ecommerce: {
                currency: 'NOK',
                value: newItem.price || 0,
                items: [newItem]
            }
        });
    } catch (error) {
        console.error('Error pushing view_item event to dataLayer:', error);
    }
}

export async function beginAltProductReplaceedAsync(product: SimpleProductExt | ProductSearchResultExt | ProductListProduct) {
    try {
        await sendToDataLayerAsync({
            event: 'begin_alt_product_replaced',
            original_item_id: product.ItemId || 'Unknown'
        });
    } catch (error) {
        console.error('Error pushing begin_alt_product_replaced event to dataLayer:', error);
    }
}

export async function altProductReplacedAsync(
    product: SimpleProductExt | ProductSearchResultExt | ProductListProduct,
    altProduct: SimpleProductExt | ProductSearchResultExt | ProductListProduct,
    recommendationType: string,
    position: number
) {
    try {
        await sendToDataLayerAsync({
            event: 'alt_product_replaced',
            original_item_id: product.ItemId || 'Unknown',
            original_item_classification: product.Classification || 'Unknown',
            alt_item_id: altProduct.ItemId || 'Unknown',
            alt_item_classification: altProduct.Classification || 'Unknown',
            recommendation_type: recommendationType,
            position: position + 1
        });
    } catch (error) {
        console.error('Error pushing alt_product_replaced event to dataLayer:', error);
    }
}
